import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import type {
  CheckpointRevertResolution,
  CheckpointRevertResult,
} from '@crowdex/workspace-protocol';
import {
  checkpointRevertTurn,
  messageCheckpoint,
  rewindChanges,
} from '@/lib/conversations/message-changes';
import type { ConversationScope } from '@/lib/desktop-shell/conversation-scope';
import { resolveWorkspaceClient, type WorkspaceClient } from '@/lib/workspaces/workspace-client';

export type MessageChangeControls = {
  /** The unified diff of what the message's turn changed, optionally for one file. */
  loadMessageDiff: (message: CrowdexUIMessage, path?: string) => Promise<string>;
  /**
   * Undoes the message's turn. Refused, writing nothing, while a file someone else may have
   * changed is not decided in `resolution`: the result lists them.
   */
  undoMessageChanges: (
    message: CrowdexUIMessage,
    resolution?: CheckpointRevertResolution,
  ) => Promise<CheckpointRevertResult>;
  /**
   * What restoring the files of the turns after the conversation's edited message would put
   * back, and risk.
   */
  planRewind: (editedIndex: number) => Promise<CheckpointRevertResult | null>;
  /**
   * Sends the conversation's edited message (its `pendingRewind`) once the user chose what
   * happens to the files the turns after it changed: restored first (with the conflicts decided),
   * or kept (`null`). Either way those turns leave its transcript, unless the restore is refused:
   * then nothing happens and the refused result, with its conflicts, is returned.
   */
  resendEdited: (
    restore: CheckpointRevertResolution | null,
  ) => Promise<CheckpointRevertResult | null>;
};

/**
 * Reads and reverts a turn's changes through its checkpoint, on the workspace server that took
 * it (this machine's or the cloud runtime's), in the folder of the conversation the message is
 * in, read when it is asked (`ConversationScope.files`). The workspace server checks for
 * conflicts and writes in one step.
 */
export function createMessageChangeControls({
  conversation,
}: {
  conversation: ConversationScope;
}): MessageChangeControls {
  async function withClient<T>(operation: (client: WorkspaceClient) => Promise<T>): Promise<T> {
    const { root: workspace, cloudRuntime } = conversation.files();
    if (workspace === null) throw new Error('No workspace is open.');
    const client = resolveWorkspaceClient(cloudRuntime, workspace);
    try {
      await client.ready;
      return await operation(client);
    } finally {
      client.close();
    }
  }

  function checkpointOf(message: CrowdexUIMessage) {
    const checkpoint = messageCheckpoint(message);
    if (checkpoint === null) throw new Error('This message has no checkpoint.');
    return checkpoint;
  }

  async function revertRewind(
    editedIndex: number,
    options: CheckpointRevertResolution & { dryRun?: boolean },
  ): Promise<CheckpointRevertResult | null> {
    const changes = rewindChanges(conversation.messages(), editedIndex);
    if (changes === null) return null;
    return await withClient((client) => client.revertCheckpoints(changes.turns, options));
  }

  return {
    async loadMessageDiff(message, path) {
      const { before, after, foreign } = checkpointOf(message);
      return await withClient((client) => client.checkpointDiff(before, after, path, foreign));
    },
    async undoMessageChanges(message, resolution = {}) {
      const turn = checkpointRevertTurn(checkpointOf(message));
      return await withClient((client) => client.revertCheckpoints([turn], resolution));
    },
    planRewind: (editedIndex) => revertRewind(editedIndex, { dryRun: true }),
    async resendEdited(restore) {
      const rewind = conversation.state().pendingRewind;
      if (rewind === null) return null;
      if (restore !== null) {
        const result = await revertRewind(rewind.editedIndex, restore);
        if (result !== null && !result.applied) return result;
      }
      conversation.setMessages((current) => current.slice(0, rewind.editedIndex));
      // It leaves in its own conversation once that one's agent is ready, shown or not.
      conversation.update({
        pendingRewind: null,
        pendingSend: { kind: 'edited', message: rewind.message, ready: true },
      });
      return null;
    },
  };
}
